import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import { useLocationKey } from '~/hooks/useLocationKey';

import { NEXT_BATCH_SIZE } from '../constants/rail';
import { getNextBatchOffset } from '../utils/railBatches';
import { createRailQueryOptions, type RailQueryParams } from '../utils/railQueryOptions';

type UseRailContestsParams = Omit<RailQueryParams, 'userId' | 'locationKey'> & {
  fillTarget: number;
  isEnabled: boolean;
};

export type UseRailContestsReturn = {
  contests: Contest[];
  isInitialLoading: boolean;
  isFetching: boolean;
  isEmptyAfterError: boolean;
  isFetchingNextPage: boolean;
  canLoadMore: boolean;
  canFetchNextBatch: boolean;
  loadMore: () => void;
  skeletonCount: number;
  isKnownEmpty: boolean;
};

const NO_CONTESTS: Contest[] = [];

export const useRailContests = ({
  slug,
  carousel,
  tab,
  firstBatchSize,
  fillTarget,
  isEnabled,
}: UseRailContestsParams): UseRailContestsReturn => {
  const { user } = useContext(UserContext);
  const { locationKey, isLocationReady } = useLocationKey();
  const hasFirstBatchSize = firstBatchSize > 0;
  const isQueryEnabled = isEnabled && isLocationReady && hasFirstBatchSize;
  const isAwaitingFirstBatchSize = isEnabled && !hasFirstBatchSize;

  const { queryKey, queryFn, getNextPageParam, retry, retryDelay, staleTime, cacheTime } =
    createRailQueryOptions({
      slug,
      carousel,
      tab,
      userId: user?.id ?? null,
      locationKey,
      firstBatchSize,
    });

  const { data, isInitialLoading, isError, isFetching, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey,
      queryFn,
      getNextPageParam,
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: isQueryEnabled,
      refetchOnWindowFocus: false,
    });

  const contests = useMemo(
    () => data?.pages.flatMap((page) => page.contests) ?? NO_CONTESTS,
    [data]
  );

  const totalIds = tab.contest_ids.length;
  const lastPage = data?.pages[data.pages.length - 1];
  const nextBatchOffset = lastPage ? getNextBatchOffset(lastPage, totalIds) : undefined;
  const remainingIds = nextBatchOffset === undefined ? 0 : totalIds - nextBatchOffset;

  const hasFetchedEveryId = lastPage !== undefined && nextBatchOffset === undefined;
  const canLoadMore = remainingIds > 0 && !isError;
  const canFetchNextBatch = isQueryEnabled && canLoadMore && !isFetching;
  const shouldFillRail = canFetchNextBatch && contests.length < fillTarget;

  const fetchNextBatch = useCallback(
    () => fetchNextPage({ cancelRefetch: false }),
    [fetchNextPage]
  );

  const loadMore = useCallback(() => {
    if (canFetchNextBatch) fetchNextBatch();
  }, [canFetchNextBatch, fetchNextBatch]);

  // nextBatchOffset re-runs the fill per landed batch; React can batch away the in-flight render.
  useEffect(() => {
    if (shouldFillRail) fetchNextBatch();
  }, [shouldFillRail, nextBatchOffset, fetchNextBatch]);

  return {
    contests,
    isInitialLoading: isInitialLoading || isAwaitingFirstBatchSize,
    isFetching,
    isEmptyAfterError: isError && contests.length === 0,
    isFetchingNextPage,
    canLoadMore,
    canFetchNextBatch,
    loadMore,
    skeletonCount: isFetchingNextPage ? Math.min(NEXT_BATCH_SIZE, remainingIds) : 0,
    isKnownEmpty: hasFetchedEveryId && contests.length === 0,
  };
};
